/**
 * What happens to the board, and to the score, when a piece has locked.
 *
 * Two resolutions, chosen by `config.groupGravity`:
 *
 * - **Off.** Full rows are removed and everything above drops straight down.
 *   The clear scores on the attack table, with back-to-back, combo and
 *   perfect-clear bonuses.
 * - **On.** Full rows are removed, then four-connected groups fall as rigid
 *   units and the board is checked again. Repeat until nothing more clears.
 *   The result scores on the chain table and nothing else — a lone clear is
 *   worth zero, which is the point of the mode.
 */
import { clearRows, fullRows, isBoardEmpty, settleGroups } from './board.js';
import {
  BACK_TO_BACK_BONUS,
  PERFECT_CLEAR_BONUS,
  baseAttack,
  chainAttack,
  comboBonus,
  isBackToBackClear,
} from './scoring.js';
import type { ClearOutcome, GameState } from './types.js';

/**
 * Clear whatever is full, score it, and update the running counters on `state`.
 *
 * `tSpin` is decided by the caller — `lock.ts` applies the three-corner rule
 * before the piece is written to the board, because writing it changes the
 * corners.
 */
export function resolveClears(state: GameState, tSpin: boolean): ClearOutcome {
  const board = state.board;
  let rows = fullRows(board);
  const clearedRows = [...rows];

  if (rows.length === 0) {
    // A placement that clears nothing ends any combo.
    state.combo = -1;
    return {
      lines: 0,
      clearedRows,
      chain: 0,
      tSpin,
      perfectClear: false,
      attack: 0,
      combo: state.combo,
      backToBack: state.backToBack,
    };
  }

  let lines = 0;
  let chain = 0;
  let attack: number;

  if (state.config.groupGravity) {
    while (rows.length > 0) {
      chain += 1;
      lines += rows.length;
      clearRows(board, rows);
      settleGroups(board);
      rows = fullRows(board);
    }
    attack = chainAttack(chain);
    // The chain table replaces combo scoring outright.
    state.combo = -1;
  } else {
    chain = 1;
    lines = rows.length;
    clearRows(board, rows);

    attack = baseAttack(lines, tSpin);
    const keepsBackToBack = isBackToBackClear(lines, tSpin);
    if (keepsBackToBack && state.backToBack) attack += BACK_TO_BACK_BONUS;
    state.backToBack = keepsBackToBack;

    state.combo += 1;
    attack += comboBonus(state.combo);
  }

  const perfectClear = isBoardEmpty(board);
  if (perfectClear && !state.config.groupGravity) attack += PERFECT_CLEAR_BONUS;

  state.lines += lines;
  state.attack += attack;
  if (chain > state.bestChain) state.bestChain = chain;

  return {
    lines,
    clearedRows,
    chain,
    tSpin,
    perfectClear,
    attack,
    combo: state.combo,
    backToBack: state.backToBack,
  };
}
